/**
 * URL helpers for the agent mode panel (split-pane trailing slot).
 * Panel visibility is driven by a query param (`agent-mode` or `leo`
 * depending on the leoRebrand segment flag) so open/close participates
 * in browser history.
 */

const DEFAULT_PARAM_KEY = 'agent-mode';

/**
 * Returns whether the agent mode panel is considered open from the route's query.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @param {string} [paramKey='agent-mode']
 * @returns {boolean}
 */
export function isAgentModePanelOpen (route, paramKey = DEFAULT_PARAM_KEY) {
    const value = route?.query?.[paramKey];

    return Boolean(value);
}

/**
 * Returns the value of the panel query param, or null if absent.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @param {string} [paramKey='agent-mode']
 * @returns {string | null}
 */
export function getAgentModePanelValue (route, paramKey = DEFAULT_PARAM_KEY) {
    const value = route?.query?.[paramKey];

    return value ?? null;
}

/**
 * Opens the agent mode panel by adding/updating the panel query param.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {string} [value='open']
 * @param {{ replace?: boolean, paramKey?: string }} [options]
 */
export function openAgentModePanel (router, route, value = 'open', options = {}) {
    const { replace = false, paramKey = DEFAULT_PARAM_KEY } = options;
    const method = replace ? 'replace' : 'push';

    router[method]({
        path: route.path,
        query: {
            ...route.query,
            [paramKey]: value,
        },
    });
}

/**
 * Returns a copy of the query object with the panel param and `expanded` removed.
 * @param {Record<string, string>} [query={}]
 * @param {string} [paramKey='agent-mode']
 * @returns {Record<string, string>}
 */
export function stripAgentModePanelParams (query = {}, paramKey = DEFAULT_PARAM_KEY) {
    const { [paramKey]: _panelParam, expanded: _expanded, ...rest } = query;

    return rest;
}

/**
 * Closes the agent mode panel by removing the panel query param.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ replace?: boolean, paramKey?: string }} [options]
 */
export function closeAgentModePanel (router, route, options = {}) {
    const { replace = false, paramKey = DEFAULT_PARAM_KEY } = options;
    const method = replace ? 'replace' : 'push';

    router[method]({
        path: route.path,
        query: stripAgentModePanelParams(route.query, paramKey),
    });
}

/**
 * Returns whether the agent mode panel is expanded from the route's query.
 * @param {import('vue-router').RouteLocationNormalizedLoaded | undefined} route
 * @returns {boolean}
 */
export function isAgentModePanelExpanded (route) {
    return 'expanded' in (route?.query || {});
}

/**
 * Toggles the `expanded` query param for the agent mode panel.
 * Adds it when absent, removes it when present.
 * @param {import('vue-router').Router} router
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ replace?: boolean }} [options]
 */
export function toggleAgentModePanelExpanded (router, route, options = {}) {
    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';
    const query = route.query || {};
    const isExpanded = 'expanded' in query;

    if (isExpanded) {
        const { expanded: _expanded, ...rest } = query;

        router[method]({
            path: route.path,
            query: rest,
        });
    } else {
        router[method]({
            path: route.path,
            query: {
                ...query,
                expanded: 'true',
            },
        });
    }
}
